/**
 * Watchlist Redux slice.
 *
 * Manages the set of instruments currently subscribed to the live tick feed.
 *
 * - `scripIds`          — numeric IDs used to filter incoming WebSocket ticks.
 * - `scrips`            — full scrip objects with live `ltp`, `volume`, `oi`
 *                         and `ltpDirection` ('up'|'down') for colour coding.
 * - `newScrips`         — scrips added in the current session but not yet
 *                         persisted to the user's saved watchlist.
 * - `selectedWatchlist` — the currently active watchlist option.
 *
 * `updateScripsReducer` is the hot path: called every 100 ms from the
 * WebSocket saga to update LTP values in-place without re-allocating the
 * array.
 */
import { createSlice } from "@reduxjs/toolkit";

/** Runtime state for a single watchlist instrument including live quote data. */
export interface ScripState {
  id: number;
  symbol: string;
  segment?: string;
  ltp?: number | null;
  prevLtp?: number | null;
  ltpDirection?: 'up' | 'down' | null;
  volume?: number | null;
  oi?: number | null;
}

/** A watchlist option for the dropdown selector. */
export interface WatchlistOption {
  label: string;
  value: string;
  scrips: string[];
}

/** Shape of the watchlist state tree. */
export interface WatchlistState {
  scripIds: number[];
  scrips: ScripState[];
  newScrips: ScripState[];
  selectedWatchlist: WatchlistOption;
}

const initialWatchlistState: WatchlistState = {
  scripIds: [],
  scrips: [],
  newScrips: [],
  selectedWatchlist: null,
};

export const watchlistSlice = createSlice({
  name: "watchlist",
  initialState: initialWatchlistState,
  reducers: {
    addScripIdsReducer: (state, action) => {
      state.scripIds = action.payload;
    },
    addScripsReducer: (state, action) => {
      const scrips = [];
      (action.payload || []).forEach((scrip) => {
        scrips.push({
          id: scrip.id,
          symbol: scrip.symbol,
          segment: scrip.segment,
          ltp: scrip.ltp ?? null,
          volume: scrip.volume ?? null,
          oi: scrip.oi ?? null,
        });
      });
      state.scrips = scrips;
    },
    removeScripReducer: (state, action) => {
      const index = state.scrips.findIndex(
        (ticker) => ticker.id === action.payload.id
      );
      state.scrips.splice(index, 1);
    },
    removeAllScripsReducer: (state) => {
      state.scrips = [];
    },
    removeScripIdsReducer: (state, action) => {
      const index = state.scripIds.findIndex(
        (scrip) => scrip === action.payload.id
      );
      state.scripIds.splice(index, 1);
    },
    removeAllScripIdsReducer: (state) => {
      state.scripIds = [];
    },
    addNewScripsReducer: (state, action) => {
      (action.payload || []).forEach((scrip) => {
        state.newScrips.push(scrip);
      });
    },
    removeNewScripReducer: (state, action) => {
      const index = state.newScrips.findIndex(
        (ticker) => ticker.id === action.payload.id
      );
      state.newScrips.splice(index, 1);
    },
    removeAllNewScripsReducer: (state) => {
      state.newScrips = [];
    },
    updateScripsReducer: (state, action) => {
      // payloadBody is string[][] — [scripId, ts, ltq, volume, oi, ltp]
      // Indexes match the TICK_* constants in websocket-handler.ts.
      const tickDataArray: string[][] = action.payload || [];
      const scripById = new Map<number, ScripState>();
      for (const scrip of state.scrips) {
        scripById.set(scrip.id, scrip);
      }

      tickDataArray.forEach((tick) => {
        const scripId = parseInt(tick[0], 10);
        const scrip = scripById.get(scripId);
        if (scrip) {
          const newLtp = parseFloat(tick[5]);
          if (!isNaN(newLtp)) {
            if (scrip.ltp != null && newLtp !== scrip.ltp) {
              scrip.ltpDirection = newLtp > scrip.ltp ? 'up' : 'down';
            }
            scrip.prevLtp = scrip.ltp;
            scrip.ltp = newLtp;
          }
          const newVol = parseFloat(tick[3]);
          if (!isNaN(newVol)) scrip.volume = newVol;
          const newOi = parseFloat(tick[4]);
          if (!isNaN(newOi)) scrip.oi = newOi;
        }
      });
    },
    updateSelectedWatchlistReducer: (state, action) => {
      state.selectedWatchlist = action.payload;
    },
    reorderScripsReducer: (state, action) => {
      const { fromIndex, toIndex } = action.payload;
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= state.scrips.length || toIndex >= state.scrips.length) return;
      const [moved] = state.scrips.splice(fromIndex, 1);
      state.scrips.splice(toIndex, 0, moved);
      // Keep scripIds in sync
      state.scripIds = state.scrips.map(s => s.id);
    },
  },
});

export const {
  addScripIdsReducer,
  addScripsReducer,
  removeScripReducer,
  removeAllScripsReducer,
  removeScripIdsReducer,
  removeAllScripIdsReducer,
  addNewScripsReducer,
  removeNewScripReducer,
  removeAllNewScripsReducer,
  updateScripsReducer,
  updateSelectedWatchlistReducer,
  reorderScripsReducer,
} = watchlistSlice.actions;
export default watchlistSlice.reducer;
